import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'
import { isExternalVideoUrl } from '../../utils/videoUrl'
import VideoUrlFrame from './VideoUrlFrame'
import WebsiteUrlCard from './WebsiteUrlCard'

export default function ImageGalleryModal({ isOpen, images = [], onClose, startIndex = 0, sectionFormStyling, section, useDefaultStyling = true, sectionType = 'image-gallery-modal' }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const onCloseRef = useRef(onClose)
  const mainVideoRef = useRef(null)

  // Update ref when onClose changes
  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const boxStyle = isFormStyling ? {
    backgroundColor: isHovered ? (formColors?.hoverBackgroundColor || formColors?.backgroundColor || DEFAULT_FORM_COLORS.hoverBackgroundColor) : (formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor),
    color: isHovered ? (formColors?.hoverTextColor || formColors?.textColor || DEFAULT_FORM_COLORS.hoverTextColor) : (formColors?.textColor || DEFAULT_FORM_COLORS.textColor)
  } : undefined
  const navButtonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : undefined

  const hasImages = Array.isArray(images) && images.length > 0
  const currentImage = hasImages ? images[currentIndex] : null

  const handleNext = useCallback(() => {
    if (!hasImages) return
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
  }, [hasImages, images.length])

  const handlePrev = useCallback(() => {
    if (!hasImages) return
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
  }, [hasImages, images.length])

  useEffect(() => {
    if (!isOpen || !hasImages) return

    const handleGlobalKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        e.stopPropagation()
        handleNext()
      }
      if (e.key === 'ArrowLeft') {
        e.stopPropagation()
        handlePrev()
      }
      if (e.key === 'Escape') {
        e.stopPropagation()
        onCloseRef.current()
      }
    }

    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [isOpen, hasImages, handleNext, handlePrev])

  useEffect(() => {
    if (isOpen && hasImages) {
      const idx = Math.max(0, Math.min(startIndex || 0, images.length - 1))
      setCurrentIndex(idx)
    }
  }, [isOpen, startIndex, hasImages, images.length])

  if (!isOpen || !hasImages || !currentImage) return null

  const imageUrl = currentImage.image?.url || currentImage.url
  const videoUrl = currentImage.video?.url || currentImage.videoUrl
  const linkUrl = currentImage.link?.url
  const isVideo = !!videoUrl
  const isExternalVideo = isExternalVideoUrl(currentImage.video || currentImage.videoUrl)

  useEffect(() => {
    const v = mainVideoRef.current
    if (!v) return
    if (isVideo && videoUrl) {
      try {
        v.load()
        v.currentTime = 0
        const p = v.play()
        if (p && typeof p.then === 'function') p.catch(() => {})
        console.log('[ImageGalleryModal] main video load/play triggered', videoUrl)
      } catch (err) {
        console.warn('[ImageGalleryModal] failed to load/play main video', err)
      }
    }
  }, [videoUrl])

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        e.stopPropagation()
        onClose()
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="relative rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={boxStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="absolute inset-0 bg-white/0 pointer-events-none" />
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full z-10 transition-colors"
          aria-label="Close gallery"
          style={navButtonStyle}
          onMouseEnter={(e) => {
            if (isButtonStyling) {
              e.currentTarget.style.backgroundColor = sectionFormStyling.hoverButtonBackgroundColor || sectionFormStyling.buttonBackgroundColor
              e.currentTarget.style.color = sectionFormStyling.hoverButtonTextColor || sectionFormStyling.buttonTextColor
            }
          }}
          onMouseLeave={(e) => {
            if (isButtonStyling) {
              e.currentTarget.style.backgroundColor = sectionFormStyling.buttonBackgroundColor
              e.currentTarget.style.color = sectionFormStyling.buttonTextColor
            }
          }}
        >
          <FiX size={24} />
        </button>

        {/* Image counter */}
        <div className="absolute top-4 left-4 bg-gray-800 text-white px-3 py-2 rounded-full text-sm font-medium z-10">
          {currentIndex + 1} / {images.length}
        </div>

        {/* Main image/video container */}
        <div className="flex-1 flex items-center justify-center bg-gray-900 overflow-hidden">
          {linkUrl ? (
            <WebsiteUrlCard url={linkUrl} className="max-w-full max-h-full" />
          ) : isVideo && (currentImage.video?.embedUrl || isExternalVideo) ? (
            <VideoUrlFrame video={currentImage.video || { url: videoUrl, videoUrl, source: 'url', provider: 'direct' }} className="max-w-full max-h-full" />
          ) : isVideo ? (
            <video
              ref={mainVideoRef}
              key={videoUrl}
              src={videoUrl}
              controls
              className="max-w-full max-h-full"
              onPlay={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          ) : imageUrl ? (
            <img
              src={imageUrl}
              alt={`Gallery ${currentIndex + 1}`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.preventDefault()} // Prevent any default navigation
              onError={(e) => console.error('Gallery image failed to load:', imageUrl)}
              draggable={false} // Prevent dragging
            />
          ) : (
            <div className="text-white text-center">
              <p>No image available</p>
            </div>
          )}
        </div>

        {/* Navigation buttons - Always show for multiple images */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handlePrev()
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full transition-colors z-10"
              aria-label="Previous image"
              style={navButtonStyle}
              onMouseEnter={(e) => {
                if (isButtonStyling) {
                  e.currentTarget.style.backgroundColor = sectionFormStyling.hoverButtonBackgroundColor || sectionFormStyling.buttonBackgroundColor
                  e.currentTarget.style.color = sectionFormStyling.hoverButtonTextColor || sectionFormStyling.buttonTextColor
                }
              }}
              onMouseLeave={(e) => {
                if (isButtonStyling) {
                  e.currentTarget.style.backgroundColor = sectionFormStyling.buttonBackgroundColor
                  e.currentTarget.style.color = sectionFormStyling.buttonTextColor
                }
              }}
            >
              <FiChevronLeft size={24} />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation()
                handleNext()
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full transition-colors z-10"
              aria-label="Next image"
              style={navButtonStyle}
              onMouseEnter={(e) => {
                if (isButtonStyling) {
                  e.currentTarget.style.backgroundColor = sectionFormStyling.hoverButtonBackgroundColor || sectionFormStyling.buttonBackgroundColor
                  e.currentTarget.style.color = sectionFormStyling.hoverButtonTextColor || sectionFormStyling.buttonTextColor
                }
              }}
              onMouseLeave={(e) => {
                if (isButtonStyling) {
                  e.currentTarget.style.backgroundColor = sectionFormStyling.buttonBackgroundColor
                  e.currentTarget.style.color = sectionFormStyling.buttonTextColor
                }
              }}
            >
              <FiChevronRight size={24} />
            </button>
          </>
        )}

        {/* Thumbnail navigation - For better UX with many images */}
        {images.length > 1 && (
          <div className="bg-gray-900 border-t border-gray-700 p-4 overflow-x-auto">
            <div className="flex gap-2 justify-start md:justify-center">
              {images.map((img, idx) => {
                const thumbUrl = img.image?.url || img.url
                return (
                  <button
                    key={thumbUrl || idx}
                    onClick={(e) => {
                      e.stopPropagation()
                      setCurrentIndex(idx)
                    }}
                    className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-colors ${
                      idx === currentIndex ? 'border-blue-500' : 'border-gray-700 hover:border-gray-600'
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                    style={isButtonStyling ? { backgroundColor: sectionFormStyling.buttonBackgroundColor, color: sectionFormStyling.buttonTextColor } : undefined}
                    onMouseEnter={(e) => {
                      if (isButtonStyling) {
                        e.currentTarget.style.backgroundColor = sectionFormStyling.hoverButtonBackgroundColor || sectionFormStyling.buttonBackgroundColor
                        e.currentTarget.style.color = sectionFormStyling.hoverButtonTextColor || sectionFormStyling.buttonTextColor
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (isButtonStyling) {
                        e.currentTarget.style.backgroundColor = sectionFormStyling.buttonBackgroundColor
                        e.currentTarget.style.color = sectionFormStyling.buttonTextColor
                      }
                    }}
                  >
                    {thumbUrl ? (
                      <img
                        src={thumbUrl}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                        <span className="text-xs text-gray-400">Video</span>
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}






ImageGalleryModal.propTypes = {
  isOpen: PropTypes.bool,
  images: PropTypes.arrayOf(PropTypes.object),
  onClose: PropTypes.func.isRequired,
  startIndex: PropTypes.number,
  sectionFormStyling: PropTypes.object,
  section: PropTypes.shape({
    _id: PropTypes.string
  }),
  sectionType: PropTypes.string
}

// import React, { useState, useEffect } from 'react'
// import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi'

// export default function ImageGalleryModal({ isOpen, images = [], onClose, startIndex = 0 }) {
//   const [currentIndex, setCurrentIndex] = useState(0)

//   if (!isOpen || !images || images.length === 0) return null

//   const currentImage = images[currentIndex]

//   const handleNext = () => {
//     setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0))
//   }

//   const handlePrev = () => {
//     setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1))
//   }

//   const handleKeyDown = (e) => {
//     if (e.key === 'ArrowRight') handleNext()
//     if (e.key === 'ArrowLeft') handlePrev()
//     if (e.key === 'Escape') onClose()
//   }

//   useEffect(() => {
//     if (isOpen) {
//       window.addEventListener('keydown', handleKeyDown)
//       return () => window.removeEventListener('keydown', handleKeyDown)
//     }
//   }, [isOpen, images.length])

//   useEffect(() => {
//     if (isOpen && images && images.length > 0) {
//       const idx = Math.max(0, Math.min(startIndex || 0, images.length - 1))
//       setCurrentIndex(idx)
//     }
//   }, [isOpen, startIndex, images])

//   const imageUrl = currentImage.image?.url || currentImage.url
//   const isVideo = !imageUrl && (currentImage.video?.url || currentImage.videoUrl)
//   const videoUrl = currentImage.video?.url || currentImage.videoUrl

//   return (
//     <div
//       className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
//       onClick={onClose}
//     >
//       <div
//         className="relative bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col"
//         onClick={(e) => e.stopPropagation()}
//       >
//         {/* Close button */}
//         <button
//           onClick={onClose}
//           className="absolute top-4 right-4 bg-gray-800 hover:bg-gray-700 text-white p-2 rounded-full z-10 transition-colors"
//           aria-label="Close gallery"
//         >
//           <FiX size={24} />
//         </button>

//         {/* Image counter */}
//         <div className="absolute top-4 left-4 bg-gray-800 text-white px-3 py-2 rounded-full text-sm font-medium z-10">
//           {currentIndex + 1} / {images.length}
//         </div>

//         {/* Main image/video container */}
//         <div className="flex-1 flex items-center justify-center bg-gray-900 overflow-hidden">
//           {isVideo ? (
//             <video
//               src={videoUrl}
//               controls
//               className="max-w-full max-h-full"
//               onPlay={(e) => e.stopPropagation()}
//             />
//           ) : imageUrl ? (
//             <img
//               src={imageUrl}
//               alt={`Gallery ${currentIndex + 1}`}
//               className="max-w-full max-h-full object-contain"
//             />
//           ) : (
//             <div className="text-white text-center">
//               <p>No image available</p>
//             </div>
//           )}
//         </div>

//         {/* Navigation buttons - Always show for multiple images */}
//         {images.length > 1 && (
//           <>
//             <button
//               onClick={handlePrev}
//               className="absolute left-4 top-1/2 -translate-y-1/2 bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-full transition-colors z-10"
//               aria-label="Previous image"
//             >
//               <FiChevronLeft size={24} />
//             </button>

//             <button
//               onClick={handleNext}
//               className="absolute right-4 top-1/2 -translate-y-1/2 bg-gray-800 hover:bg-gray-700 text-white p-3 rounded-full transition-colors z-10"
//               aria-label="Next image"
//             >
//               <FiChevronRight size={24} />
//             </button>
//           </>
//         )}

//         {/* Thumbnail navigation - For better UX with many images */}
//         {images.length > 1 && (
//           <div className="bg-gray-900 border-t border-gray-700 p-4 overflow-x-auto">
//             <div className="flex gap-2 justify-start md:justify-center">
//               {images.map((img, idx) => {
//                 const thumbUrl = img.image?.url || img.url
//                 return (
//                   <button
//                     key={idx}
//                     onClick={() => setCurrentIndex(idx)}
//                     className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden border-2 transition-colors ${
//                       idx === currentIndex ? 'border-blue-500' : 'border-gray-700 hover:border-gray-600'
//                     }`}
//                     aria-label={`Go to image ${idx + 1}`}
//                   >
//                     {thumbUrl ? (
//                       <img
//                         src={thumbUrl}
//                         alt={`Thumbnail ${idx + 1}`}
//                         className="w-full h-full object-cover"
//                       />
//                     ) : (
//                       <div className="w-full h-full bg-gray-700 flex items-center justify-center">
//                         <span className="text-xs text-gray-400">Video</span>
//                       </div>
//                     )}
//                   </button>
//                 )
//               })}
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   )
// }